// components/BlogTeaser.tsx
"use client";

import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  useTheme,
  alpha,
  Skeleton,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  limit,
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import { Post } from "@/types/post";

// Gradient options for blog cards
const gradientOptions = [
  "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
  "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
  "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
  "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
  "linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)",
];

export default function BlogTeaser() {
  const theme = useTheme();
  const [blogPosts, setBlogPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const calculateReadTime = (content: string) => {
    const wordsPerMinute = 200;
    const words = content.split(/\s+/).length;
    const minutes = Math.ceil(words / wordsPerMinute);
    return `${minutes} min read`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getCategoryFromTags = (focus_keywords: string[] = []) => {
    return focus_keywords.length > 0 ? focus_keywords[0] : "Blog Post";
  };

  const fetchBlogPosts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const q = query(
        collection(db, "posts"),
        where("status", "==", "PUBLISHED"),
        orderBy("publishedAt", "desc"),
        limit(3)
      );

      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        setBlogPosts([]);
        return;
      }

      const postsData: Post[] = querySnapshot.docs.map((doc) => {
        const postData = doc.data();
        return {
          id: doc.id,
          slug: doc.id,
          title: postData.title || "Untitled Post",
          excerpt: postData.excerpt || "",
          content: postData.content || "",
          status: postData.status || "DRAFT",
          featuredImage: postData.featuredImage || "",
          featuredAlt: postData.featuredAlt || "",
          focus_keywords: postData.focus_keywords || [],
          metaDescription: postData.metaDescription || "",
          publishedAt: postData.publishedAt || new Date().toISOString(),
        };
      });

      setBlogPosts(postsData);
    } catch (error) {
      console.error("Fetch error:", error);
      setError("Failed to load blog posts. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBlogPosts();
  }, [fetchBlogPosts]);

  if (error) {
    // Don't render the section if there's an error to avoid breaking the page
    return null;
  }

  return (
    <Box
      sx={{
        py: { xs: 6, md: 10 },
        background: `linear-gradient(135deg, ${alpha(
          theme.palette.primary.main,
          0.02
        )} 0%, ${alpha(theme.palette.secondary.main, 0.02)} 100%)`,
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ textAlign: "center", mb: { xs: 4, md: 8 } }}>
          <Typography
            variant="h2"
            gutterBottom
            sx={{
              fontWeight: 700,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              color: "transparent",
              fontSize: { xs: "2.5rem", md: "3rem" },
            }}
          >
            Latest Insights
          </Typography>
          <Typography
            variant="h6"
            component={"p"}
            color="text.secondary"
            sx={{
              maxWidth: 600,
              mx: "auto",
              fontSize: { xs: "1.1rem", md: "1.25rem" },
              lineHeight: 1.6,
            }}
          >
            Stay updated with the latest trends, technologies, and best
            practices in web development and automation
          </Typography>
        </Box>

        {/* Loading State */}
        {loading ? (
          <Grid container spacing={4}>
            {[1, 2, 3].map((item) => (
              <Grid size={{ xs: 12, md: 4 }} key={item}>
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    background: "white",
                    border: `1px solid ${alpha(
                      theme.palette.primary.main,
                      0.1
                    )}`,
                  }}
                >
                  <Skeleton variant="rectangular" height={200} />
                  <CardContent sx={{ p: 3, flexGrow: 1 }}>
                    <Skeleton
                      variant="rectangular"
                      width={100}
                      height={24}
                      sx={{ mb: 2 }}
                    />
                    <Skeleton variant="text" height={32} sx={{ mb: 2 }} />
                    <Skeleton variant="text" height={20} sx={{ mb: 1 }} />
                    <Skeleton variant="text" height={20} sx={{ mb: 1 }} />
                    <Skeleton variant="text" height={20} sx={{ mb: 3 }} />
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 2,
                      }}
                    >
                      <Skeleton variant="text" width={80} height={20} />
                      <Skeleton variant="text" width={60} height={20} />
                    </Box>
                    <Skeleton variant="rectangular" height={40} />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Grid container spacing={4}>
            {blogPosts.map((post, index) => (
              <Grid size={{ xs: 12, md: 4 }} key={post.id}>
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    background: "white",
                    border: `1px solid ${alpha(
                      theme.palette.primary.main,
                      0.1
                    )}`,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-8px)",
                      boxShadow: `0 20px 40px ${alpha(
                        theme.palette.primary.main,
                        0.15
                      )}`,
                    },
                  }}
                >
                  {/* Blog Image - Use gradient or featured image if available */}
                  <Box
                    sx={{
                      height: 200,
                      background: post.featuredImage
                        ? `url(${post.featuredImage}) center/cover`
                        : gradientOptions[index % gradientOptions.length],
                      position: "relative",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      "&::before": post.featuredImage
                        ? {
                            content: '""',
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background:
                              "linear-gradient(135deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.2) 100%)",
                          }
                        : {},
                    }}
                  >
                    <Typography
                      variant="h6"
                      component={"h3"}
                      sx={{
                        textAlign: "center",
                        px: 2,
                        position: "relative",
                        zIndex: 1,
                        fontWeight: 600,
                      }}
                    >
                      {post.title}
                    </Typography>
                  </Box>

                  <CardContent
                    sx={{
                      p: 3,
                      flexGrow: 1,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    {/* Category Chip */}
                    <Chip
                      label={getCategoryFromTags(post.focus_keywords)}
                      size="small"
                      sx={{
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        color: theme.palette.primary.main,
                        fontWeight: 600,
                        mb: 2,
                        alignSelf: "flex-start",
                      }}
                    />

                    {/* Blog Title */}
                    <Typography
                      variant="h6"
                      component="h3"
                      gutterBottom
                      sx={{
                        fontWeight: 600,
                        lineHeight: 1.4,
                        flexGrow: 1,
                      }}
                    >
                      {post.title}
                    </Typography>

                    {/* Blog Excerpt */}
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      paragraph
                      sx={{ lineHeight: 1.3 }}
                      fontSize={14}
                    >
                      {post.excerpt}
                    </Typography>

                    {/* Meta Information */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2,
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <CalendarTodayIcon
                          sx={{ fontSize: 16, color: "text.secondary" }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(post.publishedAt)}
                        </Typography>
                      </Box>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <AccessTimeIcon
                          sx={{ fontSize: 16, color: "text.secondary" }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {calculateReadTime(post.content)}
                        </Typography>
                      </Box>
                    </Box>

                    <Button
                      href={`/resources/blog/${post.slug}`}
                      variant="outlined"
                      endIcon={<ArrowForwardIcon />}
                      fullWidth
                      aria-label={`Read more about ${post.title}`}
                      title={`Read more about ${post.title}`}
                      sx={{
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        color: theme.palette.primary.main,
                        "&:hover": {
                          borderColor: theme.palette.primary.main,
                          backgroundColor: alpha(
                            theme.palette.primary.main,
                            0.04
                          ),
                        },
                      }}
                    >
                      Read More
                      {/* <span className="sr-only"> about {post.title}</span> */}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Don't show CTA if no posts */}
        {!loading && blogPosts.length > 0 && (
          <Box sx={{ textAlign: "center", mt: 8 }}>
            <Button
              variant="contained"
              size="large"
              endIcon={<ArrowForwardIcon />}
              href="/resources/blog"
              sx={{
                px: 4,
                py: 1.5,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: `0 8px 25px ${alpha(
                    theme.palette.primary.main,
                    0.3
                  )}`,
                },
                transition: "all 0.3s ease",
              }}
            >
              View All Articles
            </Button>
          </Box>
        )}
      </Container>
    </Box>
  );
}
